import React, { useState, useEffect, useRef } from 'react'
import {
  Award, CheckCircle, XCircle, Clock, RotateCcw,
  ChevronRight, AlertTriangle, Star, Target, Brain,
  PenTool, BookOpen, Lightbulb,
  ChevronDown, ChevronUp, Trophy, Zap, BarChart2, Youtube,
  Minus, Plus, X
} from 'lucide-react'
import { useProgressStore, PASS_SCORE, useUnitProgress } from '../store/progressStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import { useVideoStore } from '../store/videoStore'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

const UNIT_ID = 'G1-1'

interface TestQuestion {
  id: string
  number: number
  category: string
  difficulty: 'easy' | 'medium' | 'hard'
  question: string
  choices: string[]
  answer: number
  explanation: string[]
  concept: string
  misconception?: string
  misconceptionCode?: string
  learnStepId?: string
}

const TEST_QUESTIONS: TestQuestion[] = [
  {
    id: 'Q01', number: 1, category: '점·선·면', difficulty: 'easy',
    question: '다음 중 직선에 대한 설명으로 옳은 것은?',
    choices: [
      '양쪽으로 무한히 뻗어 있다',
      '두 끝점이 있다',
      '한쪽만 무한히 뻗어 있다',
      '길이를 잴 수 있다',
    ],
    answer: 0,
    explanation: [
      '직선: 양쪽으로 무한히 뻗어 있음 ✓',
      '선분: 두 끝점 사이 (길이를 잴 수 있음)',
      '반직선: 한 끝점에서 한쪽으로만 무한',
    ],
    concept: '직선·반직선·선분', misconceptionCode: 'GE01', learnStepId: 'GA01',
    misconception: '직선은 양방향 무한, 선분은 유한, 반직선은 한쪽 무한입니다.',
  },
  {
    id: 'Q02', number: 2, category: '점·선·면', difficulty: 'easy',
    question: '두 점 A, B를 지나는 서로 다른 직선의 개수는?',
    choices: ['1개', '2개', '무수히 많다', '없다'],
    answer: 0,
    explanation: [
      '두 점을 지나는 직선은 오직 1개입니다.',
      '이는 직선의 기본 성질 중 하나입니다.',
    ],
    concept: '직선의 결정', misconceptionCode: 'GE01', learnStepId: 'GA01',
    misconception: '두 점을 지나는 직선은 단 1개로 결정됩니다.',
  },
  {
    id: 'Q03', number: 3, category: '각의 종류', difficulty: 'easy',
    question: '75°는 어떤 종류의 각인가요?',
    choices: ['예각', '직각', '둔각', '평각'],
    answer: 0,
    explanation: [
      '예각: 0° < 각 < 90°',
      '75°는 0°보다 크고 90°보다 작으므로 예각 ✓',
    ],
    concept: '각의 종류', misconceptionCode: 'GE02', learnStepId: 'GA02',
    misconception: '예각: 0°~90° 미만, 직각: 90°, 둔각: 90°~180° 미만, 평각: 180°',
  },
  {
    id: 'Q04', number: 4, category: '각의 종류', difficulty: 'easy',
    question: '∠AOB = 90°일 때, ∠AOB는 어떤 각인가요?',
    choices: ['직각', '예각', '둔각', '평각'],
    answer: 0,
    explanation: [
      '직각은 정확히 90°인 각입니다.',
      '∠AOB = 90° → 직각 ✓',
    ],
    concept: '직각', misconceptionCode: 'GE02', learnStepId: 'GA02',
    misconception: '직각은 정확히 90°입니다.',
  },
  {
    id: 'Q05', number: 5, category: '각의 종류', difficulty: 'easy',
    question: '150°는 어떤 각인가요?',
    choices: ['둔각', '예각', '직각', '평각'],
    answer: 0,
    explanation: [
      '둔각: 90° < 각 < 180°',
      '150°는 90°보다 크고 180°보다 작으므로 둔각 ✓',
    ],
    concept: '각의 종류', misconceptionCode: 'GE02', learnStepId: 'GA02',
    misconception: '둔각은 90°보다 크고 180°보다 작은 각입니다.',
  },
  {
    id: 'Q06', number: 6, category: '평행선과 각', difficulty: 'medium',
    question: 'l∥m이고 두 직선을 가로지르는 직선이 있을 때, 동위각의 크기는?',
    choices: ['서로 같다', '합이 180°이다', '합이 90°이다', '서로 다르다'],
    answer: 0,
    explanation: [
      '평행선에서 동위각: 크기가 서로 같습니다.',
      '같은 위치에 있는 각도로, l∥m이면 ∠a = ∠b ✓',
    ],
    concept: '동위각', misconceptionCode: 'GE03', learnStepId: 'GA03',
    misconception: '동위각은 크기가 같습니다. 합이 180°가 되는 것은 동측 내각입니다.',
  },
  {
    id: 'Q07', number: 7, category: '평행선과 각', difficulty: 'medium',
    question: 'l∥m이고 ∠a = 70°일 때, 엇각 ∠b의 크기는?',
    choices: ['70°', '110°', '90°', '180°'],
    answer: 0,
    explanation: [
      '평행선에서 엇각: 크기가 서로 같습니다.',
      '∠b(엇각) = ∠a = 70° ✓',
    ],
    concept: '엇각', misconceptionCode: 'GE05', learnStepId: 'GA05',
    misconception: '엇각은 크기가 같습니다. 110°(=180°-70°)는 동측 내각입니다.',
  },
  {
    id: 'Q08', number: 8, category: '평행선과 각', difficulty: 'medium',
    question: 'l∥m이고 ∠a = 55°일 때, 동측 내각 ∠b의 크기는?',
    choices: ['125°', '55°', '90°', '35°'],
    answer: 0,
    explanation: [
      '동측 내각(같은 쪽 내각): 합이 180°',
      '∠b = 180° - 55° = 125° ✓',
    ],
    concept: '동측 내각', misconceptionCode: 'GE03', learnStepId: 'GA03',
    misconception: '동측 내각의 합 = 180°. ∠b = 180° - 55° = 125°',
  },
  {
    id: 'Q09', number: 9, category: '두 직선의 위치', difficulty: 'medium',
    question: '공간에서 두 직선이 만나지도 않고 평행하지도 않은 위치 관계를 무엇이라 하는가?',
    choices: ['꼬인 위치', '평행', '수직', '교차'],
    answer: 0,
    explanation: [
      '공간에서 두 직선의 위치 관계 3가지:',
      '① 한 점에서 만남',
      '② 평행 (같은 평면, 만나지 않음)',
      '③ 꼬인 위치 (만나지도 평행하지도 않음) ✓',
    ],
    concept: '꼬인 위치', misconceptionCode: 'GE04', learnStepId: 'GA04',
    misconception: '꼬인 위치는 공간(3차원)에서만 존재하는 두 직선의 위치 관계입니다.',
  },
  {
    id: 'Q10', number: 10, category: '두 직선의 위치', difficulty: 'medium',
    question: '평면에서 두 직선의 위치 관계로 가능한 것은?',
    choices: [
      '한 점에서 만남 또는 평행',
      '꼬인 위치',
      '꼬인 위치 또는 평행',
      '한 점에서 만남 또는 꼬인 위치',
    ],
    answer: 0,
    explanation: [
      '평면(2차원)에서 두 직선의 관계:',
      '① 한 점에서 만남',
      '② 평행 (만나지 않음)',
      '꼬인 위치는 3차원 공간에서만 가능 ✓',
    ],
    concept: '평면에서 두 직선', misconceptionCode: 'GE04', learnStepId: 'GA04',
    misconception: '평면에서는 꼬인 위치가 없습니다. 평행하거나 한 점에서 만나는 것만 가능합니다.',
  },
  {
    id: 'Q11', number: 11, category: '평행선과 각 활용', difficulty: 'medium',
    question: 'l∥m이고, 직선 n이 두 평행선을 가로지른다. ∠a = 65°이면 동위각은?',
    choices: ['65°', '115°', '25°', '130°'],
    answer: 0,
    explanation: [
      '동위각: l∥m이면 동위각의 크기는 같다.',
      '동위각 = 65° ✓',
    ],
    concept: '동위각 활용', misconceptionCode: 'GE03', learnStepId: 'GA05',
    misconception: '동위각은 평행선을 기준으로 같은 위치에 있으며 크기가 같습니다.',
  },
  {
    id: 'Q12', number: 12, category: '선분과 중점', difficulty: 'medium',
    question: '선분 AB의 중점 M에 대해 AM = 6 cm이면 AB의 길이는?',
    choices: ['12 cm', '6 cm', '3 cm', '18 cm'],
    answer: 0,
    explanation: [
      '중점은 선분을 이등분하는 점',
      'AM = MB = 6 cm',
      'AB = AM + MB = 6 + 6 = 12 cm ✓',
    ],
    concept: '중점', misconceptionCode: 'GE01', learnStepId: 'GA01',
    misconception: '중점은 선분을 정확히 절반으로 나눕니다. AB = 2×AM',
  },
  {
    id: 'Q13', number: 13, category: '각도 계산', difficulty: 'hard',
    question: '맞꼭지각 ∠AOC = 3x + 10°이고 ∠BOD = 70°일 때, x의 값은?',
    choices: ['20', '30', '15', '25'],
    answer: 0,
    explanation: [
      '맞꼭지각은 크기가 서로 같습니다.',
      '3x + 10 = 70',
      '3x = 60',
      'x = 20 ✓',
    ],
    concept: '맞꼭지각', misconceptionCode: 'GE02', learnStepId: 'GA02',
    misconception: '맞꼭지각: 두 직선이 만날 때 서로 마주 보는 각. 크기가 같습니다.',
  },
  {
    id: 'Q14', number: 14, category: '평행선과 각 종합', difficulty: 'hard',
    question: 'l∥m이고, ∠a = 40°, ∠b = 30°이면\n삼각형의 꼭짓점에서의 각도는?',
    choices: ['70°', '80°', '60°', '110°'],
    answer: 0,
    explanation: [
      '평행선 사이 삼각형: 엇각 활용',
      '꼭짓점 각도 = ∠a + ∠b (엇각의 합)',
      '= 40° + 30° = 70° ✓',
    ],
    concept: '평행선 사이 각도 활용', misconceptionCode: 'GE05', learnStepId: 'GA05',
    misconception: '평행선 사이 꺾인 선의 각도: 엇각 두 개의 합으로 구합니다.',
  },
  {
    id: 'Q15', number: 15, category: '종합', difficulty: 'hard',
    question: '다음 중 옳지 않은 것을 고르면?',
    choices: [
      '꼬인 위치에 있는 두 직선은 한 평면 위에 있다',
      '평행선에서 엇각의 크기는 서로 같다',
      '직각은 정확히 90°이다',
      '두 점을 지나는 직선은 오직 1개이다',
    ],
    answer: 0,
    explanation: [
      '꼬인 위치에 있는 두 직선은 같은 평면 위에 있을 수 없습니다.',
      '→ "한 평면 위에 있다"는 틀린 설명 ✓',
      '나머지 ②③④는 모두 옳은 진술입니다.',
    ],
    concept: '종합 판단', misconceptionCode: 'GE04', learnStepId: 'GA04',
    misconception: '꼬인 위치는 같은 평면 위에 놓을 수 없는 관계입니다.',
  },
]

interface Props { onGoLearn?: (stepId: string) => void }

export default function G1_1TestPage({ onGoLearn }: Props) {
  const [phase, setPhase] = useState<'intro' | 'test' | 'result'>('intro')
  const [current, setCurrent] = useState(0)
  const [answers, setAnswers] = useState<(number | null)[]>(Array(TEST_QUESTIONS.length).fill(null))
  const [seconds, setSeconds] = useState(0)
  const [expandedExpl, setExpandedExpl] = useState<Set<number>>(new Set())
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { recordTestResult, recordMisconceptions } = useProgressStore(s => ({
    recordTestResult: s.recordTestResult,
    recordMisconceptions: s.recordMisconceptions,
  }))
  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const getApprovedByTag = useVideoStore(s => s.getApprovedByTag)

  useEffect(() => {
    if (phase === 'test') {
      intervalRef.current = setInterval(() => setSeconds(s => s + 1), 1000)
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [phase])

  const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`

  const handleSubmit = () => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    const correct = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
    const sc = Math.round((correct / TEST_QUESTIONS.length) * 100)
    const wrongCodes = TEST_QUESTIONS
      .filter((q, i) => answers[i] !== q.answer && q.misconceptionCode)
      .map(q => q.misconceptionCode!)
    if (wrongCodes.length > 0) recordMisconceptions(UNIT_ID, wrongCodes)
    recordTestResult(UNIT_ID, {
      unitId: UNIT_ID,
      score: sc,
      correctCount: correct,
      totalCount: TEST_QUESTIONS.length,
      durationSec: seconds,
      answers: answers as number[],
      wrongConceptIds: TEST_QUESTIONS.filter((q, i) => answers[i] !== q.answer).map(q => q.concept),
      grade: sc >= 90 ? 'A' : sc >= 80 ? 'B' : sc >= 70 ? 'C' : sc >= 60 ? 'D' : 'F',
      passed: sc >= PASS_SCORE,
    })
    setPhase('result')
  }

  const correctCount = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
  const score = Math.round((correctCount / TEST_QUESTIONS.length) * 100)
  const passed = score >= PASS_SCORE
  const wrongQuestions = TEST_QUESTIONS.filter((_, i) => answers[i] !== TEST_QUESTIONS[i].answer)
  const answeredCount = answers.filter(a => a !== null).length

  // ── 인트로 ──────────────────────────────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="card text-center">
          <div className="text-6xl mb-4">📐</div>
          <h1 className="text-2xl font-black text-slate-800 mb-1">기본 도형 실전 TEST</h1>
          <p className="text-slate-500 mb-6 text-sm">G1-1 단원 전 범위 · {TEST_QUESTIONS.length}문항 · 25분</p>
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { icon: <PenTool size={18} className="text-sky-500" />, label: '문항 수', value: `${TEST_QUESTIONS.length}문항` },
              { icon: <Clock size={18} className="text-blue-500" />, label: '권장 시간', value: '25분' },
              { icon: <Trophy size={18} className="text-yellow-500" />, label: '합격 기준', value: `${PASS_SCORE}점` },
            ].map(item => (
              <div key={item.label} className="bg-slate-50 rounded-xl p-3">
                <div className="flex justify-center mb-1">{item.icon}</div>
                <p className="text-xs text-slate-500">{item.label}</p>
                <p className="text-sm font-black text-slate-800">{item.value}</p>
              </div>
            ))}
          </div>
          <div className="text-left bg-sky-50 border border-sky-200 rounded-xl p-4 mb-6">
            <p className="text-xs font-bold text-sky-700 mb-2">📋 출제 범위</p>
            <div className="grid grid-cols-2 gap-1 text-xs text-slate-600">
              {[
                'GA01 점·선·면의 개념',
                'GA02 각의 종류와 측정',
                'GA03 평행선과 교선',
                'GA04 두 직선의 위치 관계',
                'GA05 동위각·엇각',
              ].map(t => (
                <div key={t} className="flex items-center gap-1">
                  <span className="text-sky-400">•</span> {t}
                </div>
              ))}
            </div>
          </div>
          {unitProgress.testRecords.length > 0 && (
            <div className="mb-5 bg-sky-50 border border-sky-200 rounded-xl px-4 py-3 text-left">
              <p className="text-xs font-bold text-sky-700 mb-1">📊 이전 응시 기록</p>
              <p className="text-sm font-black text-sky-800">
                최고 점수: {unitProgress.bestScore}점 {unitProgress.passed && '🏆 합격'}
              </p>
              <p className="text-xs text-sky-600">총 {unitProgress.testRecords.length}회 응시</p>
            </div>
          )}
          <button
            onClick={() => { setPhase('test'); setCurrent(0); setSeconds(0); setAnswers(Array(TEST_QUESTIONS.length).fill(null)) }}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-sky-500 to-blue-600 text-white font-black text-lg shadow-lg hover:shadow-xl transition-all"
          >
            시험 시작 →
          </button>
        </div>
      </div>
    )
  }

  // ── 시험 ────────────────────────────────────────────────────────────────────
  if (phase === 'test') {
    const q = TEST_QUESTIONS[current]
    const answered = answers[current]

    return (
      <div className="p-4 max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-500">{current + 1} / {TEST_QUESTIONS.length}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
              q.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
              q.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700'
            }`}>
              {q.difficulty === 'easy' ? '쉬움' : q.difficulty === 'medium' ? '보통' : '어려움'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-mono font-bold text-sm bg-slate-100 text-slate-700">
            <Clock size={14} /> {fmt(seconds)}
          </div>
        </div>
        <div className="bg-slate-100 rounded-full h-1.5 mb-5 overflow-hidden">
          <div className="h-full bg-sky-500 rounded-full transition-all duration-300"
            style={{ width: `${((current + 1) / TEST_QUESTIONS.length) * 100}%` }} />
        </div>
        <div className="card mb-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-bold text-slate-400">{q.category}</span>
          </div>
          <p className="text-base font-bold text-slate-800 leading-relaxed mb-5 whitespace-pre-line">
            {q.number}. {q.question}
          </p>
          <div className="space-y-2.5">
            {q.choices.map((choice, i) => (
              <button key={i}
                onClick={() => { const next = [...answers]; next[current] = i; setAnswers(next) }}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 font-medium text-sm transition-all ${
                  answered === i
                    ? 'border-sky-500 bg-sky-50 text-sky-800 shadow-sm'
                    : 'border-slate-200 bg-white hover:border-sky-300 text-slate-700'
                }`}
              >
                <span className="font-black mr-2 text-slate-400">{['①','②','③','④'][i]}</span>{choice}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button disabled={current === 0} onClick={() => setCurrent(c => c - 1)}
            className="flex-1 py-2.5 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold text-sm disabled:opacity-40 hover:bg-slate-50">
            ← 이전
          </button>
          {current < TEST_QUESTIONS.length - 1 ? (
            <button onClick={() => setCurrent(c => c + 1)}
              className="flex-1 py-2.5 rounded-xl bg-sky-500 text-white font-bold text-sm hover:bg-sky-600">
              다음 →
            </button>
          ) : (
            <button onClick={handleSubmit}
              className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-black text-sm hover:opacity-90">
              제출하기 ✓
            </button>
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-1.5 justify-center">
          {TEST_QUESTIONS.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                i === current ? 'bg-sky-500 text-white' :
                answers[i] !== null ? 'bg-emerald-100 text-emerald-700' :
                'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}>
              {i + 1}
            </button>
          ))}
        </div>
        <p className="text-center text-xs text-slate-400 mt-2">
          답변 완료: {answeredCount}/{TEST_QUESTIONS.length}
        </p>
      </div>
    )
  }

  // ── 결과 ────────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 max-w-3xl mx-auto space-y-5">
      <div className={`card text-center border-2 ${passed ? 'border-emerald-300 bg-emerald-50' : 'border-orange-300 bg-orange-50'}`}>
        <div className="text-5xl mb-3">{passed ? '🏆' : '📚'}</div>
        <h2 className="text-3xl font-black mb-1" style={{ color: passed ? '#059669' : '#ea580c' }}>{score}점</h2>
        <p className={`text-base font-bold mb-4 ${passed ? 'text-emerald-700' : 'text-orange-700'}`}>
          {passed ? '🎉 합격! 기본 도형 완전 마스터!' : `아직 ${PASS_SCORE - score}점 부족해요. 다시 도전!`}
        </p>
        <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto mb-5">
          {[
            { label: '맞춤', value: `${correctCount}/${TEST_QUESTIONS.length}`, color: 'text-emerald-600' },
            { label: '점수', value: `${score}점`, color: passed ? 'text-emerald-600' : 'text-orange-600' },
            { label: '시간', value: fmt(seconds), color: 'text-sky-600' },
          ].map(item => (
            <div key={item.label} className="bg-white rounded-xl p-3 border border-slate-100">
              <p className="text-xs text-slate-500">{item.label}</p>
              <p className={`text-base font-black ${item.color}`}>{item.value}</p>
            </div>
          ))}
        </div>

        {/* AI 취약 개념 */}
        {predictedFragments.length > 0 && (
          <div className="text-left bg-sky-50 border border-sky-200 rounded-xl p-4 mb-4">
            <p className="text-xs font-bold text-sky-700 mb-2">⚡ AI 예상 취약 개념</p>
            <div className="space-y-1.5">
              {predictedFragments.slice(0, 2).map(f => {
                const cfg = FRAGMENT_TAG_CONFIG[f.tag] ?? FRAGMENT_TAG_CONFIG['review']
                return (
                  <div key={f.fragment.id} className={`flex items-center gap-2 p-2 rounded-lg border ${cfg.color}`}>
                    <span>{f.fragment.emoji}</span>
                    <span className="text-xs font-semibold flex-1">{f.fragment.title}</span>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        <div className="flex gap-2 mt-2">
          <button
            onClick={() => { setPhase('test'); setCurrent(0); setSeconds(0); setAnswers(Array(TEST_QUESTIONS.length).fill(null)); setExpandedExpl(new Set()) }}
            className="flex-1 py-3 rounded-xl border-2 border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50 flex items-center justify-center gap-2"
          >
            <RotateCcw size={14} /> 재시험
          </button>
          <button
            onClick={() => setPhase('intro')}
            className="flex-1 py-3 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 text-white font-bold text-sm hover:opacity-90"
          >
            처음으로
          </button>
        </div>
      </div>

      {/* 오답 노트 */}
      {wrongQuestions.length > 0 && (
        <div className="card border border-red-200">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={16} className="text-red-500" />
            <p className="text-sm font-black text-slate-800">오답 노트 ({wrongQuestions.length}문항)</p>
          </div>
          <div className="space-y-3">
            {wrongQuestions.map((q, idx) => {
              const qIdx = TEST_QUESTIONS.indexOf(q)
              const isOpen = expandedExpl.has(qIdx)
              const myAnswer = answers[qIdx]
              return (
                <div key={q.id} className="border border-red-100 rounded-xl overflow-hidden">
                  <button
                    onClick={() => {
                      const next = new Set(expandedExpl)
                      isOpen ? next.delete(qIdx) : next.add(qIdx)
                      setExpandedExpl(next)
                    }}
                    className="w-full px-4 py-3 bg-red-50 flex items-start justify-between text-left gap-2"
                  >
                    <div className="flex-1">
                      <p className="text-xs font-bold text-red-500 mb-0.5">{q.number}번 · {q.category}</p>
                      <p className="text-xs text-slate-700 font-medium line-clamp-2 whitespace-pre-line">{q.question}</p>
                    </div>
                    {isOpen ? <ChevronUp size={14} className="text-red-400 flex-shrink-0 mt-0.5" /> : <ChevronDown size={14} className="text-red-400 flex-shrink-0 mt-0.5" />}
                  </button>
                  {isOpen && (
                    <div className="px-4 py-3 space-y-2 bg-white">
                      {myAnswer !== null && (
                        <div className="flex items-start gap-2 text-xs">
                          <XCircle size={13} className="text-red-500 flex-shrink-0 mt-0.5" />
                          <span className="text-red-600 font-semibold">내 답: {['①','②','③','④'][myAnswer]} {q.choices[myAnswer]}</span>
                        </div>
                      )}
                      <div className="flex items-start gap-2 text-xs">
                        <CheckCircle size={13} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-emerald-600 font-semibold">정답: {['①','②','③','④'][q.answer]} {q.choices[q.answer]}</span>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                        <p className="text-xs font-bold text-slate-500 mb-1">📖 해설</p>
                        {q.explanation.map((line, li) => (
                          <p key={li} className="text-xs text-slate-600">{line}</p>
                        ))}
                      </div>
                      {q.misconception && (
                        <div className="bg-orange-50 rounded-lg p-2.5 border border-orange-100">
                          <p className="text-xs font-bold text-orange-500 mb-1 flex items-center gap-1">
                            <Lightbulb size={10} /> 핵심 오개념
                          </p>
                          <p className="text-xs text-orange-700">{q.misconception}</p>
                        </div>
                      )}
                      {onGoLearn && q.learnStepId && (
                        <button
                          onClick={() => onGoLearn(q.learnStepId!)}
                          className="w-full py-2 rounded-lg bg-sky-50 border border-sky-200 text-xs text-sky-700 font-bold hover:bg-sky-100 flex items-center justify-center gap-1"
                        >
                          <BookOpen size={11} /> {q.learnStepId} 개념 다시 공부하기
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* 취약점 분석 */}
      {wrongQuestions.length > 0 && (
        <div className="card border border-sky-200 bg-sky-50">
          <div className="flex items-center gap-2 mb-3">
            <Brain size={15} className="text-sky-600" />
            <p className="text-sm font-black text-sky-800">AI 취약점 분석</p>
          </div>
          <div className="space-y-2">
            {Array.from(new Set(wrongQuestions.map(q => q.learnStepId).filter(Boolean))).map(stepId => {
              const qs = wrongQuestions.filter(q => q.learnStepId === stepId)
              return (
                <div key={stepId} className="flex items-center justify-between px-3 py-2 bg-white rounded-lg border border-sky-100">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-bold text-sky-600">{stepId}</span>
                    <span className="text-xs text-slate-600">{qs[0].concept}</span>
                  </div>
                  <span className="text-xs px-2 py-0.5 bg-red-100 text-red-600 rounded-full font-bold">{qs.length}문항 오답</span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {videoModal && (
        <ConceptVideoModal
          conceptTag={videoModal.stepId}
          conceptLabel={videoModal.label}
          onClose={() => setVideoModal(null)}
        />
      )}
    </div>
  )
}
